import { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalWindowPortal } from './external-window-portal.tsx';
import {
  MEETING_COMPANION_CLOSE_EVENT,
  MEETING_COMPANION_OPEN_EVENT,
  type Meeting,
} from './meeting.ts';
import { MeetingCompanion } from './meeting-companion.tsx';
import { readStoredSize, resolveSurface, writeStoredSize, type Surface } from './surface.ts';

/** External windows do not always fire `pagehide` when closed; polled as a backstop. */
const CLOSED_POLL_MS = 1000;

/**
 * Meeting Companion host, mounted ONCE in the signed-in layout (`router.tsx`), so the window
 * survives route changes; renders nothing until the first open. Driven by two window events
 * (`meeting.ts`), no context. Ported from Relay's Interview Console host (RELAY-718/720).
 *
 * 🔴 USER ACTIVATION: `requestWindow()` and `window.open()` need the click's live transient
 * activation, which only survives synchronous listener execution. So `handleOpen` calls them
 * inline, before any await, timer or state round-trip; a rejected PiP request is not retried as
 * a popup (the activation is spent by then, the popup would be blocked), it is reported.
 *
 * One companion per tab: an open while one is showing switches it to the new meeting (the draft
 * of the old one is already saved as it is typed) and focuses the window; an open while a PiP
 * request is still in flight (a double-click) is ignored, so two windows never race.
 */
export function MeetingCompanionHost() {
  const [surface, setSurface] = useState<Surface | null>(null);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [win, setWin] = useState<Window | null>(null);
  const [error, setError] = useState<string | null>(null);

  // What the synchronous listener reads: it runs outside React's render cycle.
  const winRef = useRef<Window | null>(null);
  const surfaceRef = useRef<Surface | null>(null);
  const requesting = useRef(false);
  const requestId = useRef(0);
  const cleanup = useRef<(() => void) | null>(null);

  const teardown = useCallback((closeWindow: boolean) => {
    requestId.current += 1; // a window still in flight is closed on arrival, not adopted
    cleanup.current?.();
    cleanup.current = null;
    const w = winRef.current;
    winRef.current = null;
    if (w && closeWindow && !w.closed) {
      try {
        w.close();
      } catch (err) {
        console.warn('meeting companion: closing the window failed (already closed)', err);
      }
    }
    surfaceRef.current = null;
    setWin(null);
    setSurface(null);
    setMeeting(null);
  }, []);

  /** Own a freshly opened window: watch for it closing and remember its size. */
  const adopt = useCallback(
    (w: Window, id: number) => {
      if (id !== requestId.current || winRef.current) {
        try {
          w.close();
        } catch (err) {
          console.warn('meeting companion: closing the window failed (already closed)', err);
        }
        return;
      }
      winRef.current = w;
      setWin(w);
      setError(null);
      let resizeTimer: ReturnType<typeof setTimeout> | null = null;
      const onResize = () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (!w.closed) writeStoredSize(w.innerWidth, w.innerHeight);
        }, 300);
      };
      const onPageHide = () => teardown(false);
      w.addEventListener('resize', onResize);
      w.addEventListener('pagehide', onPageHide);
      const poll = window.setInterval(() => {
        if (w.closed) teardown(false);
      }, CLOSED_POLL_MS);
      cleanup.current = () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        window.clearInterval(poll);
        try {
          w.removeEventListener('resize', onResize);
          w.removeEventListener('pagehide', onPageHide);
        } catch (err) {
          console.warn('meeting companion: window listeners already gone', err);
        }
      };
    },
    [teardown],
  );

  useEffect(() => {
    function handleOpen(event: Event) {
      const next = (event as CustomEvent<Meeting>).detail;
      if (!next || requesting.current) return;
      const existing = winRef.current;
      const livePip = window.documentPictureInPicture?.window;
      const open =
        existing && !existing.closed ? existing : livePip && !livePip.closed ? livePip : null;
      if (open || surfaceRef.current === 'sheet') {
        setMeeting(next);
        open?.focus();
        return;
      }

      const { width, height } = readStoredSize();
      const chosen = resolveSurface();
      const id = ++requestId.current;
      surfaceRef.current = chosen;
      setMeeting(next);
      setSurface(chosen);
      setError(null);
      if (chosen === 'sheet') return;

      if (chosen === 'pip') {
        const pip = window.documentPictureInPicture;
        if (!pip) {
          surfaceRef.current = null;
          setSurface(null);
          setError('Picture-in-Picture is not available in this browser.');
          return;
        }
        // SYNCHRONOUS: nothing before this call may await, or the activation is spent.
        requesting.current = true;
        pip
          .requestWindow({ width, height, disallowReturnToOpener: true })
          .then((w) => {
            requesting.current = false;
            adopt(w, id);
          })
          .catch((err: unknown) => {
            requesting.current = false;
            if (id !== requestId.current) return;
            surfaceRef.current = null;
            setSurface(null);
            setError(
              err instanceof Error
                ? `The floating window was refused: ${err.message}`
                : 'The floating window was refused.',
            );
          });
        return;
      }

      // SYNCHRONOUS for the same reason: a popup opened outside the click is blocked.
      const popup = window.open(
        '',
        'crm-meeting-companion',
        `popup,width=${width},height=${height}`,
      );
      if (!popup) {
        surfaceRef.current = null;
        setSurface(null);
        setError('The window was blocked. Allow pop-ups for this site, or use the panel.');
        return;
      }
      try {
        popup.document.write(
          '<!doctype html><html><head><meta charset="utf-8"></head><body></body></html>',
        );
        popup.document.close();
      } catch (err) {
        console.warn('meeting companion: popup document already ready', err);
      }
      adopt(popup, id);
    }
    const handleClose = () => teardown(true);
    window.addEventListener(MEETING_COMPANION_OPEN_EVENT, handleOpen);
    window.addEventListener(MEETING_COMPANION_CLOSE_EVENT, handleClose);
    return () => {
      window.removeEventListener(MEETING_COMPANION_OPEN_EVENT, handleOpen);
      window.removeEventListener(MEETING_COMPANION_CLOSE_EVENT, handleClose);
    };
  }, [adopt, teardown]);

  useEffect(() => () => teardown(true), [teardown]);

  if (error)
    return (
      <div
        role="alert"
        className="border-danger/40 bg-card text-foreground fixed right-4 bottom-4 z-[80] max-w-sm rounded-lg border px-4 py-3 text-sm shadow-lg"
      >
        <p className="font-medium">The Meeting Companion could not open</p>
        <p className="text-muted-foreground mt-1 text-xs">{error}</p>
        <button
          type="button"
          onClick={() => setError(null)}
          className="text-muted-foreground hover:text-foreground mt-2 text-xs font-medium"
        >
          Dismiss
        </button>
      </div>
    );
  if (!surface || !meeting) return null;

  const content = (
    <MeetingCompanion key={meeting.id} meeting={meeting} onClose={() => teardown(true)} />
  );
  if (surface === 'sheet')
    return (
      <div
        className="border-border bg-background text-foreground fixed inset-y-0 right-0 z-[80] w-[440px] max-w-full border-l shadow-2xl"
        data-testid="meeting-companion-sheet"
      >
        {content}
      </div>
    );
  return (
    <ExternalWindowPortal win={win} title={`Meeting · ${meeting.title}`}>
      {content}
    </ExternalWindowPortal>
  );
}
